import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { inShell, reportRoute, onShellNavigate } from './shell';
import { standalone, goSignIn } from './standalone';
import Login from './components/auth/Login';
import DashboardPage from './components/dashboard/DashboardPage';
import MainLayout from './components/layout/MainLayout';
import JobsTable from './components/common/table/JobsTable';
import JobDetail from './features/JobDetail';
import CreateJobDescription from './features/CreateJobDescription';
import GeneratedJobDescription from './features/GeneratedJobDescription';
import JobAccessControl from './components/auth/JobAccessControl';
import TestCompletePage from './components/candidateExperience/common/TestCompletePage';
import UserDashboard from './components/dashboard/UserDashboard';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminDashboard from './dashboard/AdminDashboard';
import { ADMIN_PAGES, RETIRED_ADMIN_PAGES } from './dashboard/adminPages';
import StageInterviewTest from './components/candidateExperience/StageInterviewTest';
import StageQuestionnaire from './components/candidateExperience/StageQuestionnaire';
import ApplicationLocate from './pages/ApplicationLocate';
import OfferResponse from './pages/OfferResponse';
import HumanInterviewPanel from './features/HumanInterviewPanel';
import FinalInterviewPage from './features/HiringStages/FinalInterviewPage';
import { JobReportsList, InterviewReportDetail } from './features/InterviewReportsPage';
import TalentPool from './pages/TalentPool';
import MyInterviews from './pages/MyInterviews';
import ExitFilter from './pages/ExitFilter';
import SettingsPage from './features/settings/SettingsPage';
import PlatformHealth from './features/health/PlatformHealth';
import PipelinePage from './features/head/PipelinePage';
import { ShortlistCompare } from './features/head/ShortlistReview';
import AssessmentApprovals from './features/assessments/AssessmentApprovals';
import CampaignsPage from './features/campaigns/CampaignsPage';
import MessagesPage from './features/messages/MessagesPage';
import ReportsPage, { DailyHiringReport } from './features/reports/ReportsPage';
import { CopilotProvider } from './components/copilot/CopilotContext';

// Protected Route Component
// `ds`: the screen is on the EquaOS design system and renders straight into
// the AppShell content; without it the screen keeps its DPS styling.
const ProtectedRoute = ({ children, ds = false }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    // In the shell there is no login page here to go to; the shell signs in.
    // On the school's own Hiring app, the school's sign-in does.
    if (!token && standalone) goSignIn();
    else if (!token && !inShell) {
      navigate('/login');
    }
  }, [token, navigate]);

  if (!token) {
    return inShell ? <ShellSignedOut /> : null;
  }

  return <MainLayout ds={ds}>{children}</MainLayout>;
};

// In the shell, when the shell could not give us a session.
const ShellSignedOut = () => (
  <div className="min-h-screen flex items-center justify-center p-8 text-center text-gray-600">
    Your session has ended. Sign in again from the top bar to continue.
  </div>
);

// In the shell the shell's sign-in is the only one: Hiring's own login page
// and its front door go straight to where a signed-in person would land. The
// same on the school's own Hiring app, where the school's sign-in is the one.
const ShellHome = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  // Everyone lands on the one Dashboard (the admin Overview merged into it, 29 Oct 2026).
  return <Navigate to="/dashboard" replace />;
};

// Tells the shell where we are after every route change, so its address bar
// and back button follow. Renders nothing; does nothing outside the shell.
const ShellRouteReporter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    reportRoute(location.pathname, location.search);
  }, [location.pathname, location.search]);
  // A page picked in the ERP's sidebar, once Hiring's pages are there.
  useEffect(() => onShellNavigate((route) => navigate(route)), [navigate]);
  return null;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Create a wrapper component to handle params
const JobDetailWrapper = () => {
  const { id } = useParams();
  return (
    <JobAccessControl jobId={id}>
      <JobDetail />
    </JobAccessControl>
  );
};

function App() {
  return (
    /* Above the routes so the Copilot's conversation survives page changes. */
    <CopilotProvider>
      {inShell && <ShellRouteReporter />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={inShell || standalone ? <ShellHome /> : <Login />} />
        <Route path="/login" element={inShell || standalone ? <ShellHome /> : <Login />} />
        {/* Password resets happen at the Auth Gateway, which holds the
            credential. This module never did have the mail credentials to
            send a reset anyway. */}
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        {/* No /register route: the endpoint behind it is gone. Accounts are
            created by an administrator in the Auth Gateway, which is where the
            rest of the estate's logins already live. */}
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/test-complete" element={<TestCompletePage />} />
        {/* Enhancement #1: generic configured-pipeline stage runner */}
        <Route path="/stage-interview/:jobId/:candidateId/:stageId" element={<StageInterviewTest />} />
        {/* Questionnaire — candidate-facing form */}
        <Route path="/stage-questionnaire/:jobId/:candidateId/:stageId" element={<StageQuestionnaire />} />
        {/* A candidate answers their offer — signed link, no login (HIR-HR5) */}
        <Route path="/offer/:token" element={<OfferResponse />} />

        {/* Where a link from the payments console lands: an applicationId,
            resolved to the candidate's job pipeline. Protected - it reveals
            where a person is in a hiring process. */}
        <Route path="/applications/:applicationId" element={
          <ProtectedRoute ds>
            <ApplicationLocate />
          </ProtectedRoute>
        } />

        {/* Protected Routes */}
        <Route 
          path="/dashboard/*" 
          element={
            /* T2's dashboard (fee reminders, Excel download, offer follow-ups,
               candidate questions, stalled roles) in its own styling until it
               is redrawn on the design system. */
            <ProtectedRoute ds>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
                    
        {/* User Routes */}
        <Route path="/dashboard/usage" element={
          <ProtectedRoute ds>
            <UserDashboard />
          </ProtectedRoute>
        } />

        {/* Admin Routes: one address per admin page, each a sidebar entry
            (dashboard/adminPages.js). Retired admin addresses forward. */}
        {Object.entries(RETIRED_ADMIN_PAGES).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        {ADMIN_PAGES.map((p) => (
          <Route key={p.key} path={p.value} element={
            <ProtectedRoute ds>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </ProtectedRoute>
          } />
        ))}
        
        <Route path="/admin/final-interview/:id" element={
          <ProtectedRoute ds>
            <AdminRoute>
              <FinalInterviewPage />
            </AdminRoute>
          </ProtectedRoute>
        } />
        
        {/* HR Interview Panel — full-screen view for human interview */}
        <Route path="/jobs/:jobId/human-interview/:stageId/panel/:resumeId" element={
          <ProtectedRoute ds>
            <HumanInterviewPanel />
          </ProtectedRoute>
        } />

        {/* Jobs Routes */}
        <Route path="/jobs" element={
          <ProtectedRoute ds>
            <JobsTable />
          </ProtectedRoute>
        } />
        
        <Route path="/jobs/create" element={
          <ProtectedRoute ds>
            <CreateJobDescription />
          </ProtectedRoute>
        } />

        <Route path="/jobs/generate" element={
          <ProtectedRoute ds>
            <GeneratedJobDescription />
          </ProtectedRoute>
        } />

        <Route path="/jobs/:id" element={
          <ProtectedRoute ds>
            <JobDetailWrapper />
          </ProtectedRoute>
        } />

        {/* Interview reports — comprehensive post-interview reports */}
        <Route path="/jobs/:jobId/reports" element={
          <ProtectedRoute ds>
            <JobReportsList />
          </ProtectedRoute>
        } />
        <Route path="/jobs/:jobId/reports/:reportId" element={
          <ProtectedRoute ds>
            <InterviewReportDetail />
          </ProtectedRoute>
        } />

        {/* Talent Pool */}
        {/* @story HIR-TC1 — a panellist's assigned candidates */}
        <Route path="/my-interviews" element={
          <ProtectedRoute ds>
            <MyInterviews />
          </ProtectedRoute>
        } />

        {/* The exit filter: below a pass mark, waiting for a named conductor (HIR-TC1). */}
        <Route path="/exit-filter" element={
          <ProtectedRoute ds>
            <ExitFilter />
          </ProtectedRoute>
        } />
        <Route path="/talent-pool" element={
          <ProtectedRoute ds>
            <TalentPool />
          </ProtectedRoute>
        } />

        {/* The Head's view: the pipeline, with the shortlists HR puts forward in it
            (owner, 29 Oct 2026: "the shortlist and the pipeline should come at one
            place"). The old list address opens the Pipeline on its shortlist view. */}
        <Route path="/pipeline" element={
          <ProtectedRoute ds>
            <PipelinePage />
          </ProtectedRoute>
        } />
        <Route path="/shortlists" element={<Navigate to="/pipeline?view=awaiting" replace />} />
        <Route path="/shortlists/:jobId" element={
          <ProtectedRoute ds>
            <ShortlistCompare />
          </ProtectedRoute>
        } />

        {/* Settings Route */}
        <Route path="/settings" element={
          <ProtectedRoute ds>
            <SettingsPage />
          </ProtectedRoute>
        } />

        {/* Assessment Designer proposals: ask, save for approval, approve (HIR-HR2) */}
        <Route path="/assessment-approvals" element={
          <ProtectedRoute ds>
            <AssessmentApprovals />
          </ProtectedRoute>
        } />

        {/* Platform health — read-only observability surface */}
        {/* Messages: the inbox, what was sent and the campaigns, in one place
            (owner, 29 Oct 2026). The old addresses open their part of it. */}
        <Route path="/messages" element={
          <ProtectedRoute ds>
            <MessagesPage />
          </ProtectedRoute>
        } />
        <Route path="/campaigns" element={<Navigate to="/messages?view=campaigns" replace />} />
        {/* Reports: each report as a block, opened for any period (29 Oct 2026). */}
        <Route path="/reports" element={
          <ProtectedRoute ds>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/daily-hiring" element={
          <ProtectedRoute ds>
            <DailyHiringReport />
          </ProtectedRoute>
        } />
        <Route path="/campaigns/:id" element={
          <ProtectedRoute ds>
            <CampaignsPage />
          </ProtectedRoute>
        } />
        <Route path="/messages-sent" element={<SentRedirect />} />
        <Route path="/health" element={
          <ProtectedRoute ds>
            <PlatformHealth />
          </ProtectedRoute>
        } />
      </Routes>
      <ToastContainer />
    </CopilotProvider>
  );
}

/* The old Messages sent address, with its message filter kept. */
function SentRedirect() {
  const { search } = useLocation();
  const message = new URLSearchParams(search).get('message');
  return <Navigate to={`/messages?view=sent${message ? `&message=${encodeURIComponent(message)}` : ''}`} replace />;
}

export default App;